import React from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Leaf, Heart, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HoverVideoPreview } from "@/components/HoverVideoPreview";
import { inr, imageUrl } from "@/lib/api";
import { PRODUCT } from "@/constants/testIds";

/** Swiggy/Zomato-style list row card. Image on the right, meta on the left, +ADD overlapping bottom. */
export function ProductListCard({ product }) {
  const { getItem, addItem, setQty } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inCart = getItem("product", product.id);
  const qty = inCart?.qty || 0;
  const discount = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const primaryIdx = product.primary_image_index || 0;
  const img = imageUrl(product.images?.[primaryIdx] || product.images?.[0] || "");
  const wished = has(product.id);

  const handleAdd = () => {
    addItem({
      kind: "product",
      ref_id: product.id,
      name: product.name,
      unit_price: product.price,
      mrp: product.mrp,
      image_url: img,
      category_id: product.category_id,
      is_green_cracker: !!product.is_green_cracker,
    });
  };

  const handleWish = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.info("Please sign in to save items"); navigate("/login"); return; }
    const res = await toggle(product.id);
    if (res?.in_wishlist) toast.success("Added to wishlist");
    else toast("Removed from wishlist");
  };

  return (
    <div
      data-testid={PRODUCT.card(product.id)}
      className="bg-hyd-panel border border-hyd-border rounded-2xl p-3 flex gap-4 shadow-card-dark hover:border-hyd-gold/60 transition-colors"
    >
      {/* Left: info */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            {product.is_bestseller && <Tag color="red">Bestseller</Tag>}
            {product.is_new && <Tag color="gold">New</Tag>}
            {product.is_green_cracker && <Tag color="emerald"><Leaf className="w-2.5 h-2.5 mr-0.5" />Green</Tag>}
          </div>
          <Link to={`/products/${product.slug || product.id}`} className="block group">
            <h3 data-testid={PRODUCT.name(product.id)} className="font-display text-base sm:text-lg font-bold text-hyd-text group-hover:text-hyd-gold transition-colors line-clamp-2">
              {product.name}
            </h3>
          </Link>
          {product.short_description && (
            <p className="text-xs text-hyd-text2 mt-1 line-clamp-2">{product.short_description}</p>
          )}
        </div>

        <div className="flex items-baseline gap-2 mt-2">
          <span data-testid={PRODUCT.price(product.id)} className="text-hyd-gold font-bold text-lg">{inr(product.price)}</span>
          {discount > 0 && (
            <>
              <span className="text-hyd-text3 text-xs line-through">{inr(product.mrp)}</span>
              <span className="text-[10px] font-bold text-emerald-400">{discount}% OFF</span>
            </>
          )}
        </div>
      </div>

      {/* Right: image + add */}
      <div className="relative w-28 sm:w-32 shrink-0">
        <Link to={`/products/${product.slug || product.id}`}>
          <div className="w-full aspect-square rounded-2xl overflow-hidden bg-hyd-panel2 border border-hyd-border">
            {product.video_url ? (
              <HoverVideoPreview videoUrl={product.video_url} poster={img} alt={product.name} roundedCls="rounded-2xl" />
            ) : img ? (
              <img src={img} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-hyd-text3"><Sparkles className="w-8 h-8" /></div>
            )}
          </div>
        </Link>

        <button
          data-testid={PRODUCT.wishBtn(product.id)}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          onClick={handleWish}
          className="absolute top-1.5 left-1.5 w-6 h-6 grid place-items-center rounded-full bg-hyd-bg/70 backdrop-blur border border-hyd-border hover:border-hyd-red"
        >
          <Heart className={`w-3 h-3 ${wished ? "fill-hyd-red text-hyd-red" : "text-hyd-text2"}`} />
        </button>

        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
          {qty === 0 ? (
            <button
              data-testid={PRODUCT.addBtn(product.id)}
              onClick={handleAdd}
              className="bg-hyd-red text-white font-bold uppercase tracking-wider text-[11px] px-5 py-2 rounded-full shadow-glow-red hover:bg-hyd-redDim transition-colors animate-pop"
            >
              + Add
            </button>
          ) : (
            <div className="bg-hyd-bg border border-hyd-red text-hyd-red flex items-center rounded-full px-1 py-1 gap-1 shadow-glow-red animate-pop">
              <button data-testid={PRODUCT.decBtn(product.id)} onClick={() => setQty("product", product.id, qty - 1)} aria-label="Decrease" className="w-6 h-6 grid place-items-center rounded-full">
                <Minus className="w-3 h-3" />
              </button>
              <span data-testid={PRODUCT.qty(product.id)} className="min-w-[18px] text-center text-xs font-bold text-white">{qty}</span>
              <button data-testid={PRODUCT.incBtn(product.id)} onClick={() => setQty("product", product.id, qty + 1)} aria-label="Increase" className="w-6 h-6 grid place-items-center rounded-full">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tag({ children, color = "gold" }) {
  const cls =
    color === "gold" ? "bg-hyd-gold/15 text-hyd-gold"
    : color === "red" ? "bg-hyd-red/15 text-hyd-red"
    : "bg-emerald-500/15 text-emerald-300";
  return (
    <span className={`text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded-full inline-flex items-center ${cls}`}>
      {children}
    </span>
  );
}
